import { useState, useEffect } from "react";
import {
  Button,
  Container,
  Grid,
} from "@mui/material";

import { useAuth } from "../context/AuthContext";

import AddIcon from "@mui/icons-material/Add";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";

import AppDialog from "../components/General/Dialog/AppDialog";
import ConfirmDialog from "../components/General/Dialog/ConfirmDialog";
import ActionHeaderCard from "../components/General/ActionHeaderCard";

import LawyersTable from "../components/Lawyer/LawyersTable";
import LawyerForm from "../components/Lawyer/LawyerForm";
import PasswordModal from "../components/Lawyer/PasswordModal";
import useLawyers from "../hook/UseLawyers";
import { useToast } from "../context/ToastContext";

export default function Lawyer() {
  const { showToast } = useToast();
  const {
    lawyers,
    error,
    addLawyer,
    editLawyer,
    removeLawyer,
    pauseLawyer,
    resumeLawyer,
    loading,
  } = useLawyers();

  const { hasPermission } = useAuth();

  const [editData, setEditData] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordUserId, setPasswordUserId] = useState(null);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);
  const [openPauseConfirm, setOpenPauseConfirm] = useState(false);
  const [selectedPauseLawyer, setSelectedPauseLawyer] = useState(null);

  useEffect(() => {
    if (error) showToast(error, "error");
  }, [error, showToast]);

  const canCreate = hasPermission("crear abogado");
  const canView = hasPermission("ver abogado");

  const openCreate = () => {
    setEditData(null);
    setFormOpen(true);
  };

  const openEdit = (lawyer) => {
    setEditData({ ...lawyer });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditData(null);
  };

  const handleOpenDeleteConfirm = (id) => {
    setSelectedDeleteId(id);
    setOpenDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!selectedDeleteId) return;
    await removeLawyer(selectedDeleteId);
    setOpenDeleteConfirm(false);
    setSelectedDeleteId(null);
  };

  const handleUpdatePassword = (lawyerId) => {
    setPasswordUserId(lawyerId);
    setShowPasswordModal(true);
  };

  const handleOpenPauseConfirm = (lawyer) => {
    setSelectedPauseLawyer(lawyer);
    setOpenPauseConfirm(true);
  };

  const handleConfirmPause = async () => {
    if (!selectedPauseLawyer) return;
    try {
      await pauseLawyer(selectedPauseLawyer.id);
      showToast("Actualizaciones pausadas correctamente.", "success");
    } catch (err) {
      showToast(
        err.response?.data?.message || "Error al pausar actualizaciones.",
        "error",
      );
    } finally {
      setOpenPauseConfirm(false);
      setSelectedPauseLawyer(null);
    }
  };

  const handleResumeUpdates = async (lawyer) => {
    try {
      await resumeLawyer(lawyer.id);
      showToast("Actualizaciones reanudadas correctamente.", "success");
    } catch (err) {
      showToast(
        err.response?.data?.message || "Error al reanudar actualizaciones.",
        "error",
      );
    }
  };

  const handleSuccess = (msg) => {
    showToast(msg, "success");
    closeForm();
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <ActionHeaderCard
            icon={< EmojiPeopleIcon />}
            title="Abogados"
            description="Administrar abogados registrados."
            action={
              canCreate ? (
                <Button
                  onClick={openCreate}
                  variant="contained"
                  startIcon={<AddIcon />}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 800,
                    textTransform: "none",
                  }}
                >
                  Nuevo abogado
                </Button>
              ) : null
            }
          />
        </Grid>

        {canView && (
          <Grid size={{ xs: 12 }}>
            <LawyersTable
            loading={loading}
              lawyers={lawyers}
              onDelete={handleOpenDeleteConfirm}
              onEdit={openEdit}
              onUpdatePassword={handleUpdatePassword}
              onPauseUpdates={handleOpenPauseConfirm}
              onResumeUpdates={handleResumeUpdates}
            />
          </Grid>
        )}

        <PasswordModal
          open={showPasswordModal}
          onClose={() => setShowPasswordModal(false)}
          lawyerId={passwordUserId}
          onSuccess={handleSuccess}
          setError={(msg) => showToast(msg, "error")}
        />
      </Grid>

      <AppDialog
        open={formOpen}
        onClose={closeForm}
        maxWidth="sm"
        title={editData ? "Editar abogado" : "Nuevo abogado"}
        subtitle={
          editData
            ? "Actualizá los datos del abogado."
            : "Completá el formulario para registrar un abogado."
        }
        icon={<EmojiPeopleIcon />}
      >
        <LawyerForm
          editData={editData}
          onSuccess={handleSuccess}
          setError={(msg) => showToast(msg, "error")}
          addLawyer={addLawyer}
          editLawyer={editLawyer}
          onCancel={closeForm}
        />
      </AppDialog>

      <ConfirmDialog
        open={openDeleteConfirm}
        onClose={() => {
          setOpenDeleteConfirm(false);
          setSelectedDeleteId(null);
        }}
        onConfirm={handleDelete}
        title="Confirmar eliminación"
        description="¿Eliminar este abogado? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmColor="error"
      />

      <ConfirmDialog
        open={openPauseConfirm}
        onClose={() => {
          setOpenPauseConfirm(false);
          setSelectedPauseLawyer(null);
        }}
        onConfirm={handleConfirmPause}
        title="Pausar actualizaciones"
        description={
          selectedPauseLawyer
            ? `Los expedientes de ${selectedPauseLawyer.name} ${selectedPauseLawyer.lastname} seguirán visibles pero no se consultarán al sistema externo ni se podrá generar trabajo nuevo sobre ellos (adjuntos, cédulas, operaciones, liquidaciones) hasta reanudar.`
            : ""
        }
        confirmText="Pausar"
        cancelText="Cancelar"
        confirmColor="warning"
      />
    </Container>
  );
}
